/**
 * Order what is missing — `docs/visit-page.md` §8.4 (Board 3 B, C) and, while
 * purchasing is not switched on, §27.3 (Board 18 B, C).
 *
 * Ordering writes a PO line linked to the visit's material line, through
 * `PurchasingPort` (§27.1): the module sends it to the vendor; the stand-in
 * writes the same `purchase_orders` / `po_lines` rows marked `noted` and sends
 * nothing. The sheet is one sheet; Board 18 B is Board 3 B with a band, two
 * fields, the price they gave and the button changed.
 */
import { z } from 'zod';
/** *How it gets there*: *Crew picks up · Delivered to the site · Delivered to the yard*. */
export const ORDER_HOWS = ['pickup', 'site', 'yard'];
const money = z.string().trim().regex(/^\d+(\.\d{1,2})?$/, 'A price, like 689.00.');
// ---------------------------------------------------------------------------
// GET /visits/:id/materials/:line/order-options
// ---------------------------------------------------------------------------
/**
 * `?vendorId=` and `?qty=` re-price the internal line for the vendor and the
 * quantity picked; `?price=` is the price the vendor gave (Board 18 B, while
 * purchasing is off). The page sends what is typed and draws what comes back.
 */
export const orderOptionsQuerySchema = z.object({
    vendorId: z.string().uuid().optional(),
    qty: z.coerce.number().positive().max(1_000_000_000).optional(),
    price: money.optional(),
});
// ---------------------------------------------------------------------------
// POST /visits/:id/materials/:line/order
// ---------------------------------------------------------------------------
/**
 * Board 18 B's two optional fields and the price they gave — sent only while
 * `purchasing.orders` is off (the server refuses them otherwise).
 */
export const notedOrderSchema = z.object({
    /** **Who you spoke to** (optional) — *Dana at the counter*. */
    vendorContact: z.string().trim().max(200).default(''),
    /** **Their order number** (optional) — *55102*. */
    vendorRef: z.string().trim().max(120).default(''),
    /** *price they gave* — per unit. */
    price: money.optional(),
});
/**
 * `{mode:'add', poId} | {mode:'new', vendorId, how, neededAt, pickupUserId,
 * pickupAt}` + `qty` (§5.2) — **Add to PO-1051** / **Place order**; with
 * purchasing off **Note it on PO-1051** / **Note the order** (§27.3).
 */
export const orderSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('add'),
        version: z.number().int().min(1),
        poId: z.string().uuid(),
        qty: z.number().positive().max(1_000_000_000),
        noted: notedOrderSchema.optional(),
    }),
    z.object({
        mode: z.literal('new'),
        version: z.number().int().min(1),
        vendorId: z.string().uuid(),
        how: z.enum(ORDER_HOWS),
        neededAt: z.string().datetime({ offset: true }),
        /** *Picks up* — a pickup only. */
        pickupUserId: z.string().uuid().nullish(),
        /** *When* — a pickup only. */
        pickupAt: z.string().datetime({ offset: true }).nullish(),
        qty: z.number().positive().max(1_000_000_000),
        noted: notedOrderSchema.optional(),
    }),
]);
/**
 * `POST /visits/:id/materials/:line/order/remove` — **Take it off the order**
 * (Board 18 C: voids the PO line, the line goes back to *To order*, nothing is
 * sent) and the toast's **Undo** (Board 3 C: removes the PO line and re-sends
 * a sent PO). No body beyond the line's version.
 */
export const removeFromOrderSchema = z.object({
    version: z.number().int().min(1),
});
// ---------------------------------------------------------------------------
// POST /purchase-orders/:id/move — the stand-in's three moves (§27.3)
// ---------------------------------------------------------------------------
/** *Mark ready for pickup · Mark delivered to the site · Mark delivered to the yard*. */
export const PO_MOVES = ['ready_for_pickup', 'delivered_site', 'delivered_yard'];
/**
 * `{to}` (§27.3) — stand-in only, while `purchasing.orders` is off; it asks
 * nothing more and keeps who and when. `visitId` names the visit page it was
 * pressed on, so the answer is that page's Materials tab.
 */
export const movePurchaseOrderSchema = z.object({
    to: z.enum(PO_MOVES),
    visitId: z.string().uuid(),
});
//# sourceMappingURL=order.js.map